import { useEffect, useMemo, useState } from "react";
import { IoMdClose } from "react-icons/io";
import Loader from "component/loader";
import currencyPairCheck from "utilities/currencyPairCheck";
import CurrencyToggle from "../currencyToggle";
import { useSelector } from "react-redux";
import useGetRequest from "../../hooks/useGetRequest";
import { fetchTransferFees, fetchCryptoRate } from "../../api/fetchers";
import { formatMoney } from "utilities/formatMoney";

export default function SendMoneyModal({
    isOpen,
    onClose,
    beneficiary,
    onContinue,
    currency = "NGN",
    minAmount = 0,
}) {
    const [amount, setAmount] = useState("");
    const [fee, setFee] = useState(0);
    const [feeLoading, setFeeLoading] = useState(false);

    const [fxRate, setFxRate] = useState(null);
    const [rateLoading, setRateLoading] = useState(false);

    const [rateKey, setRateKey] = useState("");
    const [feeError, setFeeError] = useState("");

    const [feeRules, setFeeRules] = useState({
        min: 0,
        max: 0,
        tax: 0,
        dailyLimit: 0,
        monthlyLimit: 0,
        dailyUsed: 0,
        monthlyUsed: 0,
    });

    const { business, currency: defaults } = useSelector((state) => state);
    const isBusinessApproved = business?.status === "APPROVED";
    const [fetchAccount, accountResponse] = useGetRequest();

    const currentCurrency = (defaults || currency || "").toUpperCase();
    const destFiat = "NGN";
    const isCrypto = ["USDT", "USDC"].includes(currentCurrency);
    const currencySymbol = isCrypto ? "" : currencyPairCheck(currentCurrency);

    // Account/balance
    useEffect(() => {
        if (!business?._id || !currentCurrency) return;
        fetchAccount(`/v1/wallets/account?currency=${currentCurrency}`, {
            headers: { "x-business-id": business._id },
        });
    }, [business?._id, currentCurrency]);

    const account = accountResponse || {};
    const balanceAmount = Number(account.balance ?? 0);

    // Amount parsing
    const numericAmount = useMemo(() => {
        const n = parseFloat(String(amount));
        return Number.isFinite(n) ? n : 0;
    }, [amount]);

    // Fee endpoint currencies
    const currencyTo = useMemo(
        () => (isCrypto ? destFiat : currentCurrency).toUpperCase(),
        [isCrypto, destFiat, currentCurrency]
    );

    useEffect(() => {
        if (!business?._id || !currentCurrency || !currencyTo) return;

        if (!numericAmount || numericAmount <= 0) {
            setFee(0);
            setFeeError("");
            setRateKey("");
            setFeeRules({
                min: 0,
                max: 0,
                tax: 0,
                dailyLimit: 0,
                monthlyLimit: 0,
                dailyUsed: 0,
                monthlyUsed: 0,
            });
            return;
        }

        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                setFeeLoading(true);
                setFeeError("");

                const res = await fetchTransferFees({
                    businessId: business._id,
                    params: {
                        currency: currentCurrency,
                        currencyTo,
                        amount: numericAmount,
                    },
                });

                const payload = res?.data ?? res ?? {};
                const data = payload?.data ?? {};

                if (payload?.error) {
                    throw new Error(payload?.message || "Unable to fetch transfer fees");
                }

                const d = data?.transactionFee !== undefined ? data : payload;

                const transactionFee = Number(d.transactionFee ?? 0);
                const min = Number(d.minimumTransferAmount ?? 0);
                const max = Number(d.maximumTransferAmount ?? 0);
                const tax = Number(d.taxValue ?? 0);
                const dailyUsed = Number(d.currentDailyTransfer ?? 0);
                const monthlyUsed = Number(d.currentMonthlyTransfer ?? 0);
                const dailyLimit = Number(d.dailyTransactionLimit ?? 0);
                const monthlyLimit = Number(d.monthlyTransactionLimit ?? 0);

                const rk = String(d.rateKey || "");

                if (!cancelled) {
                    setFee(Number.isFinite(transactionFee) ? transactionFee : 0);
                    setRateKey(rk);

                    setFeeRules({
                        min: Number.isFinite(min) ? min : 0,
                        max: Number.isFinite(max) ? max : 0,
                        tax: Number.isFinite(tax) ? tax : 0,
                        dailyLimit: Number.isFinite(dailyLimit) ? dailyLimit : 0,
                        monthlyLimit: Number.isFinite(monthlyLimit) ? monthlyLimit : 0,
                        dailyUsed: Number.isFinite(dailyUsed) ? dailyUsed : 0,
                        monthlyUsed: Number.isFinite(monthlyUsed) ? monthlyUsed : 0,
                    });
                }
            } catch (e) {
                if (!cancelled) {
                    setFee(0);
                    setRateKey("");
                    setFeeError(e?.message || "Unable to fetch fees right now");
                    setFeeRules({
                        min: 0,
                        max: 0,
                        tax: 0,
                        dailyLimit: 0,
                        monthlyLimit: 0,
                        dailyUsed: 0,
                        monthlyUsed: 0,
                    });
                }
            } finally {
                if (!cancelled) setFeeLoading(false);
            }
        }, 250);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [business?._id, currentCurrency, currencyTo, numericAmount]);

    useEffect(() => {
        if (!isCrypto || !business?._id) {
            setFxRate(null);
            return;
        }

        let cancelled = false;
        (async () => {
            try {
                setRateLoading(true);
                const r = await fetchCryptoRate({
                    cryptoCurrency: currentCurrency,
                    fiatCurrency: destFiat,
                    businessId: business._id,
                });
                const val = Number(r?.data?.rate ?? r?.rate ?? r?.value ?? r?.data ?? r);
                if (!cancelled && Number.isFinite(val)) setFxRate(val);
            } catch {
                if (!cancelled) setFxRate(null);
            } finally {
                if (!cancelled) setRateLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isCrypto, currentCurrency, destFiat, business?._id]);

    // Totals
    const totalToSend = useMemo(() => {
        const t = numericAmount + Number(fee || 0);
        return Number.isFinite(t) ? t : 0;
    }, [numericAmount, fee]);

    const recipientGets = useMemo(() => {
        if (!isCrypto || !fxRate) return null;
        const val = (numericAmount - Number(fee || 0)) * fxRate;
        return val > 0 ? val : 0;
    }, [isCrypto, numericAmount, fee, fxRate]);

    const apiMin = Number(feeRules.min || 0);
    const apiMax = Number(feeRules.max || 0);
    const effectiveMin = apiMin > 0 ? apiMin : Number(minAmount || 0);

    const dailyRemaining = Math.max(0, Number(feeRules.dailyLimit) - Number(feeRules.dailyUsed));
    const monthlyRemaining = Math.max(
        0,
        Number(feeRules.monthlyLimit) - Number(feeRules.monthlyUsed)
    );

    const tooLow = numericAmount > 0 && effectiveMin > 0 && numericAmount < effectiveMin;
    const tooHigh = numericAmount > 0 && apiMax > 0 && numericAmount > apiMax;

    const exceedDaily =
        numericAmount > 0 && feeRules.dailyLimit > 0 && numericAmount > dailyRemaining;
    const exceedMonthly =
        numericAmount > 0 && feeRules.monthlyLimit > 0 && numericAmount > monthlyRemaining;

    const insufficient = totalToSend > balanceAmount;

    const validationMessage = useMemo(() => {
        if (!numericAmount || numericAmount <= 0) return "";
        if (feeError) return feeError;
        if (tooLow) return `Minimum amount is ${currencySymbol}${formatMoney(effectiveMin)}`;
        if (tooHigh) return `Maximum amount is ${currencySymbol}${formatMoney(apiMax)}`;
        if (exceedDaily)
            return `Exceeds daily limit. Remaining: ${currencySymbol}${formatMoney(dailyRemaining)}`;
        if (exceedMonthly)
            return `Exceeds monthly limit. Remaining: ${currencySymbol}${formatMoney(monthlyRemaining)}`;
        if (insufficient) return "Insufficient balance";
        return "";
    }, [
        numericAmount,
        feeError,
        tooLow,
        tooHigh,
        exceedDaily,
        exceedMonthly,
        insufficient,
        currencySymbol,
        effectiveMin,
        apiMax,
        dailyRemaining,
        monthlyRemaining,
    ]);

    const disableNext =
        feeLoading ||
        (rateLoading && isCrypto) ||
        !numericAmount ||
        numericAmount <= 0 ||
        !!validationMessage;

    if (!isOpen || !beneficiary) return null;

    return (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-2xl w-full max-w-2xl p-8 relative">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-2xl text-[#101828]"
                >
                    <IoMdClose />
                </button>

                <h2 className="text-2xl font-semibold text-[#101828] mb-6">Send money</h2>

                <p className="text-[14px]">Recipient</p>
                <div className="border p-3 rounded-lg mb-6">
                    <p className="font-[450] text-[14px] text-[#161827]">{beneficiary.name}</p>
                    <p className="text-sm text-[#6F739B] text-[14px] font-[350]">
                        {beneficiary.bankName || "Tradevu"} • {beneficiary.accountNumber}
                    </p>
                </div>

                {/* Amount input */}
                <div className="relative mb-2 flex items-center border-b border-[#E4E7EC] focus-within:border-[#6F00FF]">
                    {!isCrypto && (
                        <span className="absolute bottom-6 left-0 text-[28px] font-medium text-[#383B56] pointer-events-none pl-2">
                            {currencySymbol}
                        </span>
                    )}
                    <input
                        type="number"
                        inputMode="decimal"
                        placeholder="0"
                        className={`w-full ${isCrypto ? "pl-0" : "pl-9"} text-[64px] leading-[72px] font-[350] text-[#161827] bg-transparent outline-none placeholder:text-[#C4C6DE] placeholder:font-[350] caret-[#6F00FF]`}
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        min="0"
                    />
                    <div className="absolute right-2 top-[40%] translate-y-[-50%]">
                        <CurrencyToggle
                            dropDownClassName="shadow-dropdown2 !left-0 !top-[100%] mt-1"
                            isDisabled={!isBusinessApproved}
                            fetchWallet
                            businessId={business?._id}
                        />
                    </div>
                </div>

                <hr className="w-[80%] mb-3" />

                {/* Guidance + balance */}
                <div className="flex justify-between text-sm text-[#667085] mb-4">
                    <span>
                        {validationMessage
                            ? validationMessage
                            : effectiveMin > 0 && numericAmount > 0
                              ? `Minimum amount is ${currencySymbol}${formatMoney(effectiveMin)}`
                              : " "}
                    </span>
                    <span>
                        Balance: {isCrypto ? "" : currencySymbol}
                        {formatMoney(balanceAmount)} {isCrypto ? currentCurrency : ""}
                    </span>
                </div>

                {/* Fee + totals */}
                <div className="space-y-3 mb-6">
                    <div className="flex justify-between items-center text-[#161827] text-[16px] font-[400]">
                        <span className="text-[#667085]">Fee{Number(fee) === 0 && " (Free)"}</span>
                        <span>
                            {feeLoading ? (
                                <Loader size="sm" />
                            ) : isCrypto ? (
                                `${formatMoney(fee)} ${currentCurrency}`
                            ) : (
                                `${currencySymbol}${formatMoney(fee)}`
                            )}
                        </span>
                    </div>

                    <div className="flex justify-between items-center text-[#161827] text-[16px] font-[400]">
                        <span className="text-[#667085]">Total to deduct</span>
                        <span>
                            {isCrypto ? "" : currencySymbol}
                            {formatMoney(totalToSend)} {isCrypto ? currentCurrency : ""}
                        </span>
                    </div>

                    {(feeRules.dailyLimit > 0 || feeRules.monthlyLimit > 0) && (
                        <div className="text-xs text-[#667085] space-y-1">
                            {feeRules.dailyLimit > 0 && (
                                <div>
                                    Daily remaining: {currencySymbol}
                                    {formatMoney(dailyRemaining)}
                                </div>
                            )}
                            {feeRules.monthlyLimit > 0 && (
                                <div>
                                    Monthly remaining: {currencySymbol}
                                    {formatMoney(monthlyRemaining)}
                                </div>
                            )}
                        </div>
                    )}

                    {isCrypto && (
                        <div className="flex justify-between items-center text-[#161827] text-[16px] font-[400]">
                            <span className="text-[#667085]">Rate</span>
                            <span>
                                {rateLoading
                                    ? "Fetching rate…"
                                    : fxRate
                                      ? `1 ${currentCurrency} = ${formatMoney(fxRate)} ${destFiat}`
                                      : "—"}
                            </span>
                        </div>
                    )}
                </div>

                {/* Crypto recipient gets */}
                {isCrypto && (
                    <>
                        <p className="text-[14px] mb-1">Recipient gets</p>
                        <div className="flex items-end justify-between mb-4">
                            <div className="text-[56px] font-[350] leading-none">
                                {formatMoney(recipientGets || 0)}
                            </div>
                            <div className="text-[#667085] text-sm mb-2">{destFiat}</div>
                        </div>
                    </>
                )}

                <div className="flex justify-end mt-6">
                    <button
                        disabled={disableNext}
                        onClick={() => {
                            if (disableNext) return;
                            onContinue({
                                amount: String(numericAmount),
                                currency: currentCurrency,
                                beneficiary,
                                rateKey,
                                fee: Number(fee || 0),
                            });
                        }}
                        className={`px-3 py-2 rounded-lg text-white font-semibold ${
                            disableNext
                                ? "bg-[#E9D7FE] cursor-not-allowed"
                                : "bg-[#6F00FF] hover:bg-[#5E01D6]"
                        }`}
                    >
                        Continue
                    </button>
                </div>
            </div>
        </div>
    );
}
